import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import SimpleMD from "@/components/app/SimpleMD";
import {
  Sparkles, Send, Copy, Check, Trash2, Bot, User, Plus, MessageSquare,
  Loader2, X, ChevronDown, Zap, ListTodo, AlertTriangle, Clock, FileText, LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";

// ── Typing indicator ─────────────────────────────────────────────────────────
const TypingDots = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 0" }} data-testid="typing-indicator">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        style={{
          width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4F46E5",
          display: "inline-block",
          animation: "cwBounce 900ms infinite",
          animationDelay: `${delay}ms`,
        }}
      />
    ))}
  </div>
);

// ── Copy button ──────────────────────────────────────────────────────────────
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      onClick={copy}
      style={{ color: "#9CA3AF", background: "none", border: "none", cursor: "pointer", padding: "2px 4px", borderRadius: 4 }}
      title="Copy"
      data-testid="copy-message-btn"
    >
      {copied ? <Check size={13} style={{ color: "#4F46E5" }} /> : <Copy size={13} />}
    </button>
  );
};

const QUICK_ACTIONS = [
  { label: "Summarise project status", icon: LayoutGrid },
  { label: "List open risks", icon: AlertTriangle },
  { label: "What tasks are overdue?", icon: Clock },
  { label: "Generate a status report", icon: FileText },
  { label: "Suggest next sprint tasks", icon: ListTodo },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// ── Generate Tasks Modal ─────────────────────────────────────────────────────
const GenerateTasksModal = ({ projects, onClose }) => {
  const [desc, setDesc] = useState("");
  const [selectedProject, setSelectedProject] = useState("none");
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);

  const generate = async () => {
    if (!desc.trim()) { toast.error("Please describe the project"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/generate-tasks", {
        project_description: desc.trim(),
        project_id: selectedProject === "none" ? null : selectedProject,
      });
      setTasks(data.tasks || []);
      setSelected(new Set((data.tasks || []).map((_, i) => i)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate tasks");
    } finally {
      setBusy(false);
    }
  };

  const addTasks = async () => {
    if (selectedProject === "none") { toast.error("Please select a project to add tasks to"); return; }
    setAdding(true);
    try {
      const toAdd = tasks.filter((_, i) => selected.has(i));
      for (const task of toAdd) {
        await api.post("/tasks", {
          title: task.title,
          description: task.description,
          priority: task.priority,
          project_id: selectedProject,
          status: "todo",
        });
      }
      toast.success(`${toAdd.length} task${toAdd.length !== 1 ? "s" : ""} added to project`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add tasks");
    } finally {
      setAdding(false);
    }
  };

  const toggleTask = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const PRIORITY_COLOR = { critical: "#FF007A", high: "#F59E0B", medium: "#4F46E5", low: "#9CA3AF" };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      backgroundColor: "rgba(3,7,18,0.85)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12,
        width: "100%", maxWidth: 560, maxHeight: "85vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #E5E7EB" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Generate tasks from description</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer" }}><X size={16} /></button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          <label style={{ display: "block", fontSize: 12, color: "#9CA3AF", marginBottom: 6, fontWeight: 500 }}>Project description</label>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Describe your project or feature in a few sentences…"
            autoFocus
            rows={4}
            style={{
              width: "100%", boxSizing: "border-box", resize: "vertical",
              padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
              backgroundColor: "#FFFFFF", color: "#111827", fontSize: 13, outline: "none",
              marginBottom: 14, fontFamily: "inherit",
            }}
          />
          <label style={{ display: "block", fontSize: 12, color: "#9CA3AF", marginBottom: 6, fontWeight: 500 }}>Add to project (optional)</label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
              backgroundColor: "#FFFFFF", color: "#111827", fontSize: 13, outline: "none",
              marginBottom: 16, appearance: "none",
            }}
          >
            <option value="none">Select a project…</option>
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <button
            onClick={generate}
            disabled={busy || !desc.trim()}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 16px",
              borderRadius: 8, border: "none", cursor: busy ? "not-allowed" : "pointer",
              backgroundColor: "#4F46E5", color: "#FFFFFF", fontWeight: 700, fontSize: 13,
              opacity: busy ? 0.7 : 1, marginBottom: tasks.length ? 20 : 0,
            }}
          >
            {busy ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Sparkles size={14} />}
            {busy ? "Generating…" : "Generate tasks"}
          </button>

          {tasks.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10, fontWeight: 500 }}>
                Generated tasks — select which to add:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {tasks.map((t, i) => (
                  <div
                    key={i}
                    onClick={() => toggleTask(i)}
                    style={{
                      padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                      border: selected.has(i) ? "1px solid #4F46E5" : "1px solid #E5E7EB",
                      backgroundColor: selected.has(i) ? "rgba(79,70,229,0.04)" : "#FFFFFF",
                      display: "flex", gap: 10, alignItems: "flex-start",
                    }}
                  >
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, marginTop: 2, flexShrink: 0,
                      border: selected.has(i) ? "1px solid #4F46E5" : "1px solid #374151",
                      backgroundColor: selected.has(i) ? "#4F46E5" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {selected.has(i) && <Check size={10} style={{ color: "#FFFFFF" }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 2 }}>{t.title}</div>
                      {t.description && <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5 }}>{t.description}</div>}
                      {t.priority && (
                        <span style={{
                          display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700,
                          padding: "2px 6px", borderRadius: 4, fontFamily: "monospace",
                          backgroundColor: `${PRIORITY_COLOR[t.priority] || "#9CA3AF"}15`,
                          color: PRIORITY_COLOR[t.priority] || "#9CA3AF",
                        }}>
                          {t.priority.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {tasks.length > 0 && (
          <div style={{ padding: "12px 20px", borderTop: "1px solid #E5E7EB", display: "flex", gap: 10 }}>
            <button
              onClick={addTasks}
              disabled={adding || selected.size === 0 || selectedProject === "none"}
              style={{
                flex: 1, padding: "10px", borderRadius: 8, border: "none",
                backgroundColor: (adding || selected.size === 0 || selectedProject === "none") ? "#E5E7EB" : "#4F46E5",
                color: (adding || selected.size === 0 || selectedProject === "none") ? "#9CA3AF" : "#FFFFFF",
                fontWeight: 700, fontSize: 13, cursor: (adding || selected.size === 0 || selectedProject === "none") ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {adding ? <Loader2 size={13} /> : null}
              Add {selected.size} task{selected.size !== 1 ? "s" : ""} to project
            </button>
            <button onClick={onClose} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #E5E7EB", backgroundColor: "transparent", color: "#7d8590", fontSize: 13, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Assistant component ─────────────────────────────────────────────────
const Assistant = () => {
  const { user } = useAuth();

  // Projects
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("none");

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [currentConvId, setCurrentConvId] = useState(null);
  const [messages, setMessages] = useState([]);

  // UI state
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showGenModal, setShowGenModal] = useState(false);
  const [convsLoading, setConvsLoading] = useState(true);

  const endRef = useRef(null);
  const textRef = useRef(null);

  // Fetch projects and conversations on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data || []);
      } catch { /* ignore */ }
    })();
    loadConversations();
  }, []);

  const loadConversations = async () => {
    setConvsLoading(true);
    try {
      const { data } = await api.get("/assistant/conversations");
      setConversations(data || []);
    } catch { /* ignore */ } finally {
      setConvsLoading(false);
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const openConversation = async (conv) => {
    setCurrentConvId(conv.conversation_id);
    try {
      const { data } = await api.get(`/assistant/conversations/${conv.conversation_id}`);
      const msgs = (data.messages || []).map((m, i) => ({
        id: `${m.role}_${i}`,
        role: m.role === "assistant" ? "ai" : "user",
        text: m.content,
        ts: data.updated_at,
      }));
      setMessages(msgs);
    } catch {
      toast.error("Failed to load conversation");
    }
  };

  const startNewConversation = () => {
    setCurrentConvId(null);
    setMessages([]);
    textRef.current?.focus();
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/assistant/conversations/${convId}`);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
      if (currentConvId === convId) startNewConversation();
      toast.success("Conversation deleted");
    } catch {
      toast.error("Failed to delete conversation");
    }
  };

  const send = async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || busy) return;
    setInput("");
    textRef.current?.focus();

    const ts = new Date().toISOString();
    const userMsg = { id: `u_${Date.now()}`, role: "user", text, ts };
    const next = [...messages, userMsg];
    setMessages(next);
    setBusy(true);

    try {
      const { data } = await api.post("/assistant/chat", {
        message: text,
        project_id: selectedProject === "none" ? null : selectedProject,
        conversation_id: currentConvId || null,
      });
      const aiMsg = { id: `a_${Date.now()}`, role: "ai", text: data.reply, ts: new Date().toISOString() };
      setMessages([...next, aiMsg]);
      if (data.conversation_id && data.conversation_id !== currentConvId) {
        setCurrentConvId(data.conversation_id);
        // Refresh sidebar
        loadConversations();
      }
    } catch (e) {
      const errMsg = {
        id: `e_${Date.now()}`, role: "ai",
        text: e.response?.data?.detail || "Sorry, I couldn't process that request. Please try again.",
        ts: new Date().toISOString(),
      };
      setMessages([...next, errMsg]);
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Sidebar conversation title
  const convTitle = (conv) => {
    const firstMsg = (conv.messages || [])[0];
    const text = firstMsg?.content || conv.title || "Conversation";
    return text.length > 42 ? text.slice(0, 42) + "…" : text;
  };

  return (
    <>
      <style>{`
        @keyframes cwBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .cw-assistant-input:focus { border-color: rgba(79,70,229,0.4) !important; outline: none; }
        .cw-conv-item:hover { background-color: #F3F4F6; }
        .cw-conv-delete { opacity: 0; transition: opacity 0.15s; }
        .cw-conv-item:hover .cw-conv-delete { opacity: 1; }
        .cw-quick-action:hover { border-color: rgba(79,70,229,0.35) !important; color: #111827 !important; }
      `}</style>

      {showGenModal && (
        <GenerateTasksModal projects={projects} onClose={() => { setShowGenModal(false); loadConversations(); }} />
      )}

      <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", backgroundColor: "#FFFFFF" }}>
        {/* Top bar */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 10, color: "#4F46E5", fontWeight: 700, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 2 }}>AI Project Assistant</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Ask anything.</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={() => setShowGenModal(true)}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                borderRadius: 8, border: "1px solid rgba(79,70,229,0.25)",
                backgroundColor: "rgba(79,70,229,0.06)", color: "#4F46E5",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
              data-testid="generate-tasks-btn"
            >
              <ListTodo size={14} /> Generate tasks
            </button>
          </div>
        </div>

        {/* Main 2-panel layout */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>

          {/* ── Left sidebar: conversations ── */}
          <div style={{
            width: 280, flexShrink: 0, borderRight: "1px solid #E5E7EB",
            backgroundColor: "#FFFFFF", display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            <div style={{ padding: "12px 12px 8px" }}>
              <button
                onClick={startNewConversation}
                data-testid="new-conversation-btn"
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px",
                  borderRadius: 8, border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                  color: "#111827", fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                <Plus size={14} style={{ color: "#4F46E5" }} /> New conversation
              </button>
            </div>

            {/* Project filter */}
            <div style={{ padding: "0 12px 8px" }}>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                data-testid="assistant-project-select"
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 8,
                  border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                  color: selectedProject === "none" ? "#6B7280" : "#4F46E5",
                  fontSize: 12, outline: "none", appearance: "none",
                }}
              >
                <option value="none">All projects</option>
                {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            </div>

            {/* Conversations list */}
            <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 8px" }}>
              {convsLoading ? (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#9CA3AF", fontSize: 12 }}>
                  <Loader2 size={14} style={{ animation: "spin 1s linear infinite", margin: "0 auto 6px", display: "block" }} />
                  Loading…
                </div>
              ) : conversations.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px 12px", color: "#9CA3AF", fontSize: 12 }}>
                  No conversations yet. Start chatting!
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => openConversation(conv)}
                    className="cw-conv-item"
                    data-testid={`conversation-${conv.conversation_id}`}
                    style={{
                      padding: "9px 10px", borderRadius: 8, cursor: "pointer",
                      backgroundColor: currentConvId === conv.conversation_id ? "#1c2128" : "transparent",
                      border: currentConvId === conv.conversation_id ? "1px solid #30363d" : "1px solid transparent",
                      marginBottom: 2, display: "flex", alignItems: "flex-start", gap: 8,
                    }}
                  >
                    <MessageSquare size={13} style={{ color: "#9CA3AF", flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.4, wordBreak: "break-word" }}>
                        {convTitle(conv)}
                      </div>
                      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 3, fontFamily: "monospace" }}>
                        {fmtDate(conv.updated_at || conv.created_at)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteConversation(conv.conversation_id, e)}
                      className="cw-conv-delete"
                      style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", padding: 2, flexShrink: 0 }}
                      title="Delete"
                      data-testid={`delete-conv-${conv.conversation_id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Right panel: chat ── */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* Messages area */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }} data-testid="chat-messages">
              {messages.length === 0 ? (
                <div style={{ textAlign: "center", paddingTop: 60 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 20, backgroundColor: "rgba(79,70,229,0.08)",
                    border: "1px solid rgba(79,70,229,0.15)", display: "flex", alignItems: "center",
                    justifyContent: "center", margin: "0 auto 16px",
                  }}>
                    <Sparkles size={28} style={{ color: "#4F46E5" }} />
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Your senior PM, on call.</h3>
                  <p style={{ fontSize: 13, color: "#7d8590", maxWidth: 380, margin: "0 auto 32px", lineHeight: 1.6 }}>
                    Ask for charters, RAIDs, status reports, agile coaching — anything project-related.
                  </p>
                  {/* Quick action chips */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 520, margin: "0 auto" }}>
                    {QUICK_ACTIONS.map(({ label, icon: Icon }) => (
                      <button
                        key={label}
                        onClick={() => send(label)}
                        className="cw-quick-action"
                        data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                          borderRadius: 20, border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                          color: "#9CA3AF", fontSize: 12, fontWeight: 500, cursor: "pointer",
                        }}
                      >
                        <Icon size={13} style={{ color: "#4F46E5" }} /> {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    style={{ display: "flex", gap: 12, justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 20 }}
                    data-testid={`message-${m.id}`}
                  >
                    {m.role === "ai" && (
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        backgroundColor: "rgba(79,70,229,0.12)", border: "1px solid rgba(79,70,229,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Bot size={14} style={{ color: "#4F46E5" }} />
                      </div>
                    )}
                    <div style={{ maxWidth: "78%", display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                      <div style={{
                        padding: "10px 14px", fontSize: 13, lineHeight: 1.65,
                        borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        backgroundColor: m.role === "user" ? "#E5E7EB" : "#FFFFFF",
                        border: m.role === "user" ? "none" : "1px solid #E5E7EB",
                        color: "#111827",
                      }}>
                        {m.role === "user"
                          ? <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
                          : <div className="ai-prose"><SimpleMD text={m.text} /></div>
                        }
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        {m.ts && <span style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace" }}>{fmtTime(m.ts)}</span>}
                        {m.role === "ai" && <CopyBtn text={m.text} />}
                      </div>
                    </div>
                    {m.role === "user" && (
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        backgroundColor: "#374151", display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <User size={13} style={{ color: "#9CA3AF" }} />
                      </div>
                    )}
                  </div>
                ))
              )}

              {busy && (
                <div style={{ display: "flex", gap: 12, justifyContent: "flex-start", marginBottom: 20 }} data-testid="busy-indicator">
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    backgroundColor: "rgba(79,70,229,0.12)", border: "1px solid rgba(79,70,229,0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Bot size={14} style={{ color: "#4F46E5" }} />
                  </div>
                  <div style={{
                    padding: "10px 14px", borderRadius: "16px 16px 16px 4px",
                    backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB",
                  }}>
                    <TypingDots />
                  </div>
                </div>
              )}

              <div ref={endRef} />
            </div>

            {/* Quick action chips above input (when messages exist) */}
            {messages.length > 0 && (
              <div style={{ padding: "8px 24px 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {QUICK_ACTIONS.slice(0, 3).map(({ label, icon: Icon }) => (
                  <button
                    key={label}
                    onClick={() => send(label)}
                    className="cw-quick-action"
                    style={{
                      display: "flex", alignItems: "center", gap: 5, padding: "5px 10px",
                      borderRadius: 20, border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                      color: "#7d8590", fontSize: 11, cursor: "pointer",
                    }}
                  >
                    <Icon size={11} style={{ color: "#4F46E5" }} /> {label}
                  </button>
                ))}
              </div>
            )}

            {/* Input bar */}
            <div style={{ padding: "12px 20px 16px", borderTop: "1px solid #E5E7EB", flexShrink: 0 }}>
              <div style={{ display: "flex", gap: 10 }}>
                <textarea
                  ref={textRef}
                  className="cw-assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask the AI anything about your projects… (Enter to send, Shift+Enter for newline)"
                  rows={3}
                  data-testid="chat-input"
                  style={{
                    flex: 1, resize: "none", padding: "10px 14px", borderRadius: 10,
                    border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                    color: "#111827", fontSize: 13, fontFamily: "inherit",
                    lineHeight: 1.5,
                  }}
                />
                <button
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  data-testid="send-message-btn"
                  style={{
                    padding: "0 16px", borderRadius: 10, border: "none",
                    backgroundColor: busy || !input.trim() ? "#E5E7EB" : "#4F46E5",
                    color: busy || !input.trim() ? "#9CA3AF" : "#FFFFFF",
                    cursor: busy || !input.trim() ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center",
                  }}
                >
                  {busy ? <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> : <Send size={18} />}
                </button>
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: "#9CA3AF", display: "flex", gap: 12 }}>
                <span>Shift+Enter for newline</span>
                <span>·</span>
                <span>{selectedProject === "none" ? "No project context" : "Project context active"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Assistant;
